import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useDeleteToast } from "../../stores/hooks/useDeleteToast";
import { useToasts } from "../../stores/hooks/useToasts";
import {
  ToastItem,
  ToastTypeError,
  ToastTypeInfo,
} from "../../stores/slices/toastSlice";
import { Button } from "../Button/Button";
import { usePortalNode } from "../hooks";
import { ChevronRightIcon } from "../icons/ChevronRightIcon";
import { CrossIcon } from "../icons/CrossIcon";

export const Toast: FunctionComponent = () => {
  const portalNode = usePortalNode();
  const toasts = useToasts().slice(0, 1);
  return portalNode
    ? createPortal(
        <>
          {toasts.map((toast) => (
            <Item item={toast} key={toast.message} />
          ))}
        </>,
        portalNode
      )
    : null;
};

const Item: FunctionComponent<{ item: ToastItem }> = ({ item }) => {
  const { t } = useTranslation();
  const deleteToast = useDeleteToast();

  const removeToast = (): void => {
    deleteToast(item);
  };

  return (
    <div
      className={clsx(
        "tw-toast",
        item.type === ToastTypeError && "bg-error",
        item.type === ToastTypeInfo && "bg-info"
      )}
      role="alert"
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-sm font-semibold">{t(`toast.${item.type}`)}</h4>
          <p>{t(item.message)}</p>
          {item.action && (
            <Button
              onClick={(): void => {
                if (item.action) {
                  item.action.callback();
                  removeToast();
                }
              }}
              aria-label="toastAction"
              className="my-2"
            >
              <ChevronRightIcon className="mr-2" />
              {t(item.action.label)}
            </Button>
          )}
        </div>
        <Button
          aria-label={
            item.type === ToastTypeError ? "closeErrorToast" : "closeInfoToast"
          }
          className={clsx(
            "p-0 w-10 h-10 rounded-tw-full font-thin text-2xl shrink-0",
            "-mt-2 -mr-2"
          )}
          onClick={removeToast}
        >
          <CrossIcon />
        </Button>
      </div>
    </div>
  );
};
